import { LitElement, html, PropertyValues, TemplateResult } from 'lit';
import { customElement, state, query, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { connect } from 'pwa-helpers';
import { SearchableAccordionsPanelWebComponent } from '../../gzSearchableAccordionsPanel';
import {
  SearchableAccordionsPanelViewConfigs,
  SearchableViewItem,
  SearchableViewItems,
} from '../../gzSearchableAccordionsPanel/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { translateFn } from '../../../utils/translator.helper';
import {
  AccordionsSearchState,
  selectAccordionsSearchState,
  searchStringSelector,
  activeViewSelector,
  setActiveView,
} from '@nirvana/redux/searchable-ui-accordions';
import { RootState, store } from '@nirvana/redux/store';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';

/**
 * @summary Type here the description of this component
 * @tag searchable-accordions-view
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-searchable-accordions-view')
export class SearchableAccordionsViewWebComponent extends connect(store)(LitElement) {
  @property({ type: Array, attribute: true, reflect: true })
  accordions: SearchableViewItems[] = [];

  @property({ type: String, attribute: true, reflect: true })
  public routingPath: string;

  /** The tag name of the active view web component */
  @property({ type: String, attribute: true, reflect: true })
  private activeView: string;

  @state()
  private searchString: string;

  @query('gz-searchable-accordions-panel')
  private accordionsPanel: SearchableAccordionsPanelWebComponent;

  /**
   * The property is used to pass properties that configure the gz-searchable-accordions-panel component UI (i.e. props for the search input)
   */
  @property({ type: Object, attribute: true, reflect: true })
  searchablePanelViewConfigs: SearchableAccordionsPanelViewConfigs = {
    searchStringMinLength: 0,
    searchInputPlaceholder: 'SEARCH_INPUT_PLACEHOLDER',
  };

  static get styles() {
    return styles;
  }

  private getFirstUiElementName(items: (SearchableViewItems | SearchableViewItem)[]): string | undefined {
    for (const item of items) {
      if (item.uiElementName) {
        return item.uiElementName;
      }
      // Recursively search in children if the item has them
      if ('children' in item && item.children.length > 0) {
        const result = this.getFirstUiElementName(item.children);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  }

  private getUiElementNameParentId(items: (SearchableViewItems | SearchableViewItem)[], uiElementName: string): string {
    for (const item of items) {
      if (item.uiElementName === uiElementName) {
        return item.id;
      }
      // Recursively search in children if the item has them
      if ('children' in item && item.children.length > 0) {
        const result = this.getUiElementNameParentId(item.children, uiElementName);
        if (result) {
          return item.id;
        }
      }
    }
    return '';
  }

  updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    if (changedProperties.has('accordions') && !isEmpty(this.accordions)) {
      const currentView = this.activeView ? this.activeView : this.getFirstUiElementName(this.accordions);
      this.updateCurrentView(currentView as string);

      // open the accordion panel with the current view
      this.accordionsPanel.updateComplete.then(() =>
        this.accordionsPanel.openAccordionPanelById(this.getUiElementNameParentId(this.accordions, currentView || ''))
      );
    }
  }

  private updateCurrentView(viewName: string) {
    store.dispatch(setActiveView(viewName));
  }

  stateChanged(rootState: RootState) {
    const appState: AccordionsSearchState = selectAccordionsSearchState(rootState);
    this.searchString = searchStringSelector(appState);
    this.activeView = activeViewSelector(appState);
  }

  private renderTabContent(): TemplateResult {
    if (isEmpty(this.activeView) || !customElements.get(this.activeView)) {
      return html`<div>${translateFn('Please add a valid HTML element')}</div>`;
    } else {
      const searchString =
        this.searchablePanelViewConfigs.searchStringMinLength &&
        this.searchString.length < this.searchablePanelViewConfigs.searchStringMinLength
          ? undefined
          : this.searchString;
      return staticHtml`<${unsafeStatic(this.activeView)} .searchString="${ifDefined(searchString)}"/>`;
    }
  }

  render() {
    return html`
      <gz-searchable-accordions-panel
        .accordions="${this.accordions}"
        .routingPath="${this.routingPath}"
        .activeView="${this.activeView}"
        .searchablePanelViewConfigs="${ifDefined(this.searchablePanelViewConfigs)}"
      ></gz-searchable-accordions-panel>
      <div class="view-container">${this.renderTabContent()}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-searchable-accordions-view': SearchableAccordionsViewWebComponent;
  }
}
